"use client";

/**
 * CollabV3HostNote 컴포넌트
 * @description 주최자의 말 — 길면 접고 `...See more` 로 편다(앱 `CollabHostNote`). 잘렸는지는 글자 수로
 *   짐작하지 않고 실제로 넘쳤는지를 잰다 — 예전 2.0 웹 상세의 소개글과 같은 이유다. 아래에
 *   붙은 그림(커버와 겹치지 않는 것)을 깐다.
 * @example <CollabV3HostNote text={detail.description} images={detail.descriptionImages} />
 */
import { memo, useCallback, useEffect, useRef, useState } from "react";

import { collabV3DetailStyles } from "./CollabV3Detail.styles";
import type { CollabV3HostNoteProps } from "./CollabV3Detail.types";

import { COLLAB_V3_COPY } from "../../constants/collabV3Copy";
import { AppImage } from "@/shared/ui";

/** 접었을 때 보일 줄 수 — 2.0 웹 상세와 같다 */
const COLLAPSED_LINES = 4;
const NOTE_IMAGE_SIZES = "(min-width: 768px) 240px, 50vw";
const COLLAPSED_STYLE = {
  display: "-webkit-box",
  WebkitLineClamp: COLLAPSED_LINES,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
} as const;

const CollabV3HostNote = memo<CollabV3HostNoteProps>(({ text, images }) => {
  const styles = collabV3DetailStyles.note;
  const [isExpanded, setExpanded] = useState(false);
  const [isOverflowing, setOverflowing] = useState(false);
  const paragraph = useRef<HTMLParagraphElement>(null);

  const measure = useCallback(() => {
    const node = paragraph.current;
    if (!node || isExpanded) return;
    setOverflowing(node.scrollHeight > node.clientHeight + 1);
  }, [isExpanded]);

  useEffect(() => {
    measure();
    const node = paragraph.current;
    if (!node) return;
    // 폭이 바뀌면(회전 · 창 크기 · 늦게 붙는 웹폰트) 줄 수도 바뀐다
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [measure, text]);

  const expand = useCallback(() => setExpanded(true), []);

  if (!text && images.length === 0) return null;
  return (
    <section className={styles.root}>
      {text ? (
        <p
          ref={paragraph}
          className={styles.text}
          style={isExpanded ? undefined : COLLAPSED_STYLE}
        >
          {text}
        </p>
      ) : null}
      {isOverflowing && !isExpanded ? (
        <button type="button" onClick={expand} className={styles.more}>
          {COLLAB_V3_COPY.note.readMore}
        </button>
      ) : null}
      {images.length > 0 ? (
        <div className={styles.images}>
          {images.map((url) => (
            <div key={url} className={styles.image}>
              <AppImage uri={url} sizes={NOTE_IMAGE_SIZES} />
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
});

CollabV3HostNote.displayName = "CollabV3HostNote";

export default CollabV3HostNote;
